import lodashGet from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import Text from '~/common/components/Texts/Text';

import { useQuestionLibraryStore } from '../../store/questionLibraryStore';
import formatValues from '../../utils/formatValues';

type ProcessedTextProps = {
  placeholders?: any[];
  text?: string;
  rest?: any;
  name?: string;
  as?: string;
  metadata?: { index: number };
} & Partial<React.ComponentProps<typeof Text>>;

/**
 * @typedef {object} ProcessedTextProps
 * @property {string} text
 * @property {import('./../../types/common').ElementPlaceholder[]} placeholders
 * @property {string} name
 *
 * @param {ProcessedTextProps} props
 * @returns
 */
function ProcessedText({
  text,
  placeholders,
  name = 'processedText',
  as = 'span',
  metadata,
  children,
  ...rest
}: Readonly<ProcessedTextProps>) {
  const parsedText = useMemo(() => {
    if (!placeholders || placeholders.length === 0) {
      return text;
    }
    const mappedPlaceholders = placeholders?.map((p) => p.key) ?? [];
    const placeholdersSplitRegex = new RegExp(
      `(${mappedPlaceholders.join('|')})`,
      'gi',
    );

    const splitText = text.split(placeholdersSplitRegex);

    return splitText.map((text) => {
      const placeholderData = placeholders?.find((p) => p.key === text);
      if (!placeholderData) {
        return text;
      }

      if ('formDataKey' in placeholderData) {
        return (
          <ProcessedTextSegmentWithGuid
            key={text}
            guid={placeholderData.formDataKey}
            data-testid={`${name}-${placeholderData.key}`}
            {...placeholderData}
          />
        );
      }

      if ('screenDataKey' in placeholderData) {
        return (
          <ProcessedTextSegmentWithScreenData
            key={text}
            metadata={metadata}
            screenDataKey={placeholderData.screenDataKey}
            data-testid={`${name}-${placeholderData.key}`}
            {...placeholderData}
          />
        );
      }

      return text;
    });
  }, [text, placeholders, name, metadata]);

  return (
    <Text as={as} data-testid={name} {...rest}>
      {parsedText}
      {children}
    </Text>
  );
}

export default memo(ProcessedText, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});

type ProcessedTextSegmentWithGuidProps = {
  as?: string;
  format?: string;
  guid: string;
  defaultValue?: string;
  children?: React.ReactNode;
};

function ProcessedTextSegmentWithGuid({
  as = 'span',
  guid,
  format,
  defaultValue,
  children,
  ...rest
}: Readonly<ProcessedTextSegmentWithGuidProps>) {
  const { control } = useFormContext();

  const watchedValue = useWatch({ name: guid, control });

  const displayValue = formatValues(format, watchedValue, defaultValue);

  return (
    <Text as={as as any} {...rest}>
      {displayValue}
      {children}
    </Text>
  );
}

type ProcessedTextSegmentWithScreenDataProps = {
  as?: string;
  format?: string;
  screenDataKey: string;
  suffix?: string;
  defaultValue?: string;
  metadata?: { index: number };
  children?: React.ReactNode;
};

function ProcessedTextSegmentWithScreenData({
  as = 'span',
  screenDataKey,
  format,
  suffix,
  defaultValue,
  metadata,
  children,
  ...rest
}: Readonly<ProcessedTextSegmentWithScreenDataProps>) {
  const screenDataValue = useQuestionLibraryStore(
    useShallow((store) => {
      if (!screenDataKey.includes('{i}'))
        return lodashGet(store.screenData, screenDataKey);
      const key = screenDataKey.split('{i}').join(String(metadata.index));
      return lodashGet(store.screenData, key);
    }),
  );
  if (screenDataValue === undefined || screenDataValue === null) {
    return null;
  }
  const displayValue = formatValues(format, screenDataValue, defaultValue);

  return (
    <Text as={as as any} {...rest}>
      {displayValue}
      {displayValue && suffix}
      {children}
    </Text>
  );
}
